import React from 'react'

import { Trash2 } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

import { Playlist } from '@/core/api/entities/playlist/playlist'
import { useTranslation } from '@/core/i18n/useTranslation'
import { Favorite } from '@/shared/components/atoms/Favorite'
import { Image } from '@/shared/components/atoms/Image'
import {
	ActionDropdownMenu,
	ActionMenuItem,
} from '@/shared/components/molecules/ActionDropdownMenu'
import { cn } from '@/shared/utils/helpers'

export interface PlaylistCardProps {
	playlist: Playlist
	dropdownOptions?: ActionMenuItem[]
	onFavoriteClick?: (playlist: Playlist, isFavoritesCarousel: boolean) => void
	onDelete?: (playlist: Playlist) => void
	showLocationIcon?: boolean
	showFavoriteIcon?: boolean
	className?: string
	isLoadingFavorite?: boolean
	isFavoritesCarousel?: boolean
	isFavorite?: boolean
	isCustomPlaylist?: boolean
}

const PlaylistCard: React.FC<PlaylistCardProps> = ({
	playlist,
	dropdownOptions = [],
	onFavoriteClick,
	onDelete,
	showFavoriteIcon = true,
	className = '',
	isLoadingFavorite = false,
	isFavoritesCarousel = false,
	isFavorite,
	isCustomPlaylist = false,
}) => {
	const navigate = useNavigate()
	const { t, getTranslation, currentLanguage } = useTranslation('common')

	const title = getTranslation(
		playlist?.titleTranslations || {},
		currentLanguage,
		playlist?.title || ''
	)

	const handleFavoriteClick = () => {
		if (onFavoriteClick) {
			onFavoriteClick(playlist, isFavoritesCarousel)
		}
	}

	const handleCardClick = () => {
		navigate(`/music/playlists/${playlist.id}`, {
			state: {
				isFavorite: isPlaylistFavorite,
			},
		})
	}

	const handleDeleteClick = (e: React.MouseEvent) => {
		e.stopPropagation()
		if (onDelete) {
			onDelete(playlist)
		}
	}

	// Determine if playlist is favorited: use isFavorite prop if provided, otherwise fall back to isFavoritesCarousel
	const isPlaylistFavorite =
		isFavorite !== undefined ? isFavorite : isFavoritesCarousel

	// Show badge if reviewed is false and it's a custom playlist
	const showReviewBadge = isCustomPlaylist && playlist.reviewed === false

	// When only a single delete action is needed, show a simple icon button instead of a dropdown
	const showSimpleDelete = onDelete && dropdownOptions.length === 0
	const showDropdown = dropdownOptions.length > 0

	return (
		<div
			className={cn(
				'flex flex-col m-0 h-full bg-transparent w-full lg:max-w-[200px] lg:min-w-[200px] min-w-[70px] max-w-[160px] transition-transform duration-200 ease-out cursor-pointer hover:-translate-y-[2px]',
				className
			)}
			onClick={handleCardClick}
		>
			<div
				className={cn(
					'relative w-full aspect-square lg:rounded-3xl rounded-lg',
					'border border-border/10 dark:border-none'
				)}
			>
				<Image
					src={playlist.getMediumImage() || '/default-playlist.png'}
					alt={title}
					width="100%"
					height="100%"
				/>
				<div className="absolute inset-0 flex flex-col justify-between p-2 pointer-events-none">
					<div className="flex items-start gap-1 justify-between pointer-events-auto w-full">
						{showReviewBadge ? (
							<div className="bg-[var(--color-accent-main)] rounded-lg px-2 py-1 flex items-center justify-center pointer-events-none">
								<span className="text-white text-xs font-medium whitespace-nowrap">
									{t('entities.music.ready_to_review')}
								</span>
							</div>
						) : (
							<div />
						)}
						<div className="flex items-center gap-1 ml-auto">
							{showDropdown && (
								<ActionDropdownMenu
									items={dropdownOptions}
									triggerClassName="h-8 w-8 rounded-full bg-black/30 backdrop-blur-sm hover:bg-black/60"
									triggerIconClassName="h-4 w-4 text-white"
								/>
							)}
							{showSimpleDelete && (
								<button
									onClick={handleDeleteClick}
									className="h-8 w-8 rounded-full bg-black/30 backdrop-blur-sm hover:bg-black/60 flex items-center justify-center cursor-pointer"
								>
									<Trash2 className="h-4 w-4 text-white" />
								</button>
							)}
							{showFavoriteIcon && (
								<div
									className="bg-black/30 rounded-full w-8 h-8 flex items-center justify-center backdrop-blur-sm"
									onClick={(e) => e.stopPropagation()}
								>
									<Favorite
										filled={isPlaylistFavorite}
										onClick={() => handleFavoriteClick()}
										size="small"
										filledColor="white"
										outlinedColor="white"
										disabled={isLoadingFavorite}
										isLoading={isLoadingFavorite}
									/>
								</div>
							)}
						</div>
					</div>
				</div>
			</div>
			<div className="pt-4 pb-0 flex items-start min-h-[40px]">
				<span className="lg:text-base text-xs lg:font-medium text-foreground text-ellipsis line-clamp-2 leading-[1.4] mb-6">
					{title}
				</span>
			</div>
		</div>
	)
}

export default PlaylistCard
